import en from '@/locales/en.json';
import de from '@/locales/de.json';
import es from '@/locales/es.json';
import fr from '@/locales/fr.json';
import { useConfig } from '@/context/ConfigContext';
import { translations as i18nTranslations } from '@/i18n/translations';

// Define language names for the UI
export const languageNames = {
  en: 'English',
  de: 'Deutsch',
  es: 'Español',
  fr: 'Français'
};

// Export available languages for use in other components
export const availableLanguages = Object.keys(languageNames);

// Deep merge helper so nested namespaces (e.g., tooltips) are merged instead of overwritten
const deepMerge = (base: NestedTranslation, override: NestedTranslation): NestedTranslation => {
  const result: NestedTranslation = { ...base };
  Object.keys(override).forEach((key) => {
    const baseVal = (result as any)[key];
    const overrideVal = (override as any)[key];
    if (
      baseVal && typeof baseVal === 'object' && !Array.isArray(baseVal) &&
      overrideVal && typeof overrideVal === 'object' && !Array.isArray(overrideVal)
    ) {
      (result as any)[key] = deepMerge(baseVal as NestedTranslation, overrideVal as NestedTranslation);
    } else {
      (result as any)[key] = overrideVal;
    }
  });
  return result;
};

// Merge translations from locale files with i18n translations (locale first, then i18n overrides)
const translations = {
  en: deepMerge(en as unknown as NestedTranslation, i18nTranslations.en as unknown as NestedTranslation),
  de: deepMerge(de as unknown as NestedTranslation, i18nTranslations.de as unknown as NestedTranslation),
  es: deepMerge(es as unknown as NestedTranslation, i18nTranslations.es as unknown as NestedTranslation),
  fr: deepMerge(fr as unknown as NestedTranslation, i18nTranslations.fr as unknown as NestedTranslation)
};

// Define a type for nested translation objects
type NestedTranslation = {
  [key: string]: string | number | boolean | NestedTranslation;
};

// Helper function to get nested properties using dot notation
const getNestedValue = (obj: NestedTranslation, path: string): string | number | boolean | NestedTranslation | undefined => {
  return path.split('.').reduce<string | number | boolean | NestedTranslation | undefined>((prev, curr) => {
    return prev && typeof prev === 'object' ? prev[curr] : undefined;
  }, obj as NestedTranslation);
};

export const useTranslate = () => {
  const { language } = useConfig();
  return (key: string, replacements?: Record<string, string>) => {
    let translation = getNestedValue(translations[language], key);
    if (translation !== undefined && typeof translation === 'string' && replacements) {
      Object.keys(replacements).forEach((placeholder) => {
        translation = (translation as string).replace(`{${placeholder}}`, replacements[placeholder]);
      });
    }
    return translation !== undefined ? String(translation) : key;
  };
};
